/**
 * Wider or Narrower? — pick which of two positions opens more hands.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import { type Position } from "@/features/quiz/lib/preflopRanges";
import {
  type WiderNarrowerQuestion,
  generateQuestion,
  rangeSizeOf,
} from "@/features/quiz/lib/widerNarrowerQuiz";

export function QuizWiderNarrower() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<WiderNarrowerQuestion, Position>({
      quizType: "wider_narrower",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.wider,
      getQuestionPayload: (q) => ({ a: q.a, b: q.b }),
    });

  const narrower = correctAnswer === question.a ? question.b : question.a;

  return (
    <QuizLayout
      Icon={Brain}
      title="Wider or Narrower?"
      subtitle="Two positions: which opens more hands?"
      description="Tests the core preflop principle that position dictates range width: later seats open wider. Trivial in the abstract; the questions earn their keep on edge pairs like CO vs SB or BTN vs SB where rec players often guess wrong."
      stats={stats}
    >
      <p className="text-center text-text-muted text-sm">
        Which position has a wider opening range?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {[question.a, question.b].map((pos) => (
          <QuizOption
            key={pos}
            value={pos}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
            size="lg"
          >
            <span className="text-xl tracking-wide">{pos}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              <strong>{correctAnswer}</strong> is wider than <strong>{narrower}</strong>.
            </>
          }
          rationale={
            <>
              {correctAnswer} opens {rangeSizeOf(correctAnswer)} hands vs {narrower}'s {rangeSizeOf(narrower)} ({rangeSizeOf(correctAnswer) - rangeSizeOf(narrower)} more). Later positions open wider: fewer players left to act, position postflop, mostly facing the blinds.
            </>
          }
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
